import { CameraOutlined } from '@ant-design/icons';
import { Upload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import { useMemo, useState } from 'react';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useUpdateProfileImageMutation } from '@/modules/user/userApi';
import { updateUser } from '@/store/authSlice';
import { useAppDispatch } from '@/store/hooks';
import type { User } from '@/types/auth';
import { resolveMutationResult } from '@/utils/apiResponse';
import { resolveUserAvatarUrl } from '@/utils/media';
import { getUserDisplayName } from '@/utils/user';
import styles from '../Account.module.scss';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

interface ProfileAvatarUploadProps {
  user?: User | null;
}

export function ProfileAvatarUpload({ user }: ProfileAvatarUploadProps) {
  const dispatch = useAppDispatch();
  const notify = useNotify();
  const [previewAvatar, setPreviewAvatar] = useState<string | undefined>();
  const [uploadProfileImage, { isLoading: uploading }] = useUpdateProfileImageMutation();
  useGlobalLoader(uploading);
  const displayName = getUserDisplayName(user ?? null);

  const avatarSrc = useMemo(
    () => previewAvatar ?? resolveUserAvatarUrl(user),
    [previewAvatar, user],
  );

  const handleAvatarChange = async (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) {
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify.error('Please upload a PNG, JPG, WEBP, or GIF image.');
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      notify.error('Image must be 5MB or smaller.');
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreviewAvatar(localPreview);

    const formData = new FormData();
    formData.append('image', file);

    const result = await resolveMutationResult<User>(uploadProfileImage(formData));

    if (result.ok) {
      dispatch(updateUser(result.data));
      setPreviewAvatar(resolveUserAvatarUrl(result.data));
      notify.success(result.message ?? 'Profile photo updated.');
      return;
    }

    setPreviewAvatar(resolveUserAvatarUrl(user));
    notify.error(result.message);
  };

  return (
    <div className={styles.avatarBlock}>
      <Upload
        accept={ACCEPTED_TYPES.join(',')}
        showUploadList={false}
        beforeUpload={() => false}
        onChange={handleAvatarChange}
        disabled={uploading}
      >
        <button type="button" className={styles.avatarButton} disabled={uploading}>
          <PreviewImage
            src={avatarSrc}
            alt={displayName}
            size={88}
            shape="circle"
            className={styles.userAvatar}
            stopPropagation
            fallback={displayName.charAt(0).toUpperCase()}
          />
          <span className={styles.avatarEdit}>
            <CameraOutlined />
          </span>
        </button>
      </Upload>
      <span className={styles.uploadLabel}>
        {uploading ? 'Uploading…' : 'Change photo'}
      </span>
    </div>
  );
}
